/**
 * LoyaltyWidget — coach-facing tier + discount tile.
 *
 * Shows on the coach's Settings page (Testimonial Loyalty Scheme card).
 * Pulls the live snapshot from `GET /api/account/loyalty` — which also
 * triggers server-side "anniversary transition" detection (auto-emits
 * a `loyalty_tier_advance` analytics event if the coach has crossed a
 * tier boundary since their last fetch).
 *
 * Layout:
 *   [ Tier badge + discount % ]  ← visible summary
 *   [ Progress bar: current tier → next tier over N days ]
 *   Peak-tier flourish once they hit Testimonial Coach.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Award, Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function LoyaltyWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/account/loyalty`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setData(res.data))
      .catch(() => setErrored(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card data-testid="loyalty-widget-loading">
        <CardContent className="py-8 text-center text-sm text-gray-400">Loading loyalty tier…</CardContent>
      </Card>
    );
  }
  if (errored || !data?.loyalty) return null;

  const l = data.loyalty;
  const tiers = data.tiers || [];
  const currentIdx = tiers.findIndex((t) => t.id === l.tier_id);
  const nextTier = currentIdx >= 0 ? tiers[currentIdx + 1] : tiers[0];

  // Progress fraction: how far the coach has moved from their current tier
  // anniversary towards the next one. Uses exact years for smooth fill.
  let progressPct = 0;
  if (l.at_peak) {
    progressPct = 100;
  } else if (nextTier) {
    const currentMinYears = currentIdx >= 0 ? tiers[currentIdx].min_years : 0;
    const window = nextTier.min_years - currentMinYears;
    const into = (l.years_exact || 0) - currentMinYears;
    progressPct = Math.max(0, Math.min(100, Math.round((into / window) * 100)));
  }

  const daysLine = l.at_peak
    ? 'Peak tier reached — discount locked forever.'
    : nextTier
      ? `${l.days_to_next} days to ${nextTier.title} (${nextTier.discount_pct}% off)`
      : 'Loyalty clock starts on your first renewal.';

  return (
    <Card data-testid="loyalty-widget">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {l.at_peak ? <Trophy className="w-5 h-5 text-amber-500" /> : <Award className="w-5 h-5 text-amber-500" />}
          Testimonial Loyalty Scheme
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-1">Your tier</p>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-2xl font-black text-gray-900" data-testid="loyalty-widget-tier">
                {l.title || 'Not yet a Squad Player'}
              </span>
              {l.discount_pct > 0 && (
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                  {l.discount_pct}% off
                </Badge>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {l.years} {l.years === 1 ? 'year' : 'years'} · {l.days} days coaching with us
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-3xl font-black text-amber-600 leading-none">{l.discount_pct}%</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-widest">Renewal discount</p>
          </div>
        </div>

        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-gray-600 font-semibold" data-testid="loyalty-widget-progress-line">{daysLine}</span>
            {!l.at_peak && nextTier && <span className="text-amber-600 font-black">{progressPct}%</span>}
          </div>
          <div className="h-2 rounded-full bg-amber-100 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-yellow-500 transition-all duration-500"
              style={{ width: `${progressPct}%` }}
              data-testid="loyalty-widget-progress-bar"
            />
          </div>
        </div>

        <p className="text-[11px] text-gray-500 italic">
          Every anniversary drops your renewal price by another 5% — up to 50% off at Year 10 (Testimonial Coach, locked forever).
        </p>
      </CardContent>
    </Card>
  );
}

export default LoyaltyWidget;
